// One home's gas level over 60 days, the design's levelChart: the line with
// its refills as green jumps, the 20 % threshold, and the dotted forecast to
// empty from the home's pace.
import { useEffect, useId, useRef, useState } from 'react';
import { S } from '../lib/strings';
import { dayLabel } from '../lib/format';
import { useTip } from './Tip';

interface Props { days: { day: string; level: number | null }[]; level: number; daysLeft: number | null }

export function LevelChart({ days, level, daysLeft }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(700);
  const [hi, setHi] = useState<number | null>(null);
  const tip = useTip();
  const id = 'lg' + useId().replace(/:/g, '');
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(300, el.clientWidth || 700)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Days without a reading carry the previous level, so the line has no holes.
  const ser: number[] = [];
  days.forEach((d, i) => ser.push(d.level ?? (i ? ser[i - 1] : level)));
  const n = ser.length;
  const refills = ser.map((v, i) => (i && v > ser[i - 1] + 30 ? i : -1)).filter((i) => i >= 0);
  const fd = Math.max(0, Math.min(30, daysLeft ?? 0));
  const H = 230, pl = 40, pr = 86, pt = 14, pb = 28, iw = W - pl - pr, ih = H - pt - pb;
  const tot = n - 1 + Math.ceil(fd) + 1, sx = iw / tot;
  const X = (i: number) => pl + i * sx, Y = (v: number) => pt + ih - (v / 100) * ih;
  let d = '';
  ser.forEach((v, i) => { d += (i && !refills.includes(i) ? 'L' : 'M') + X(i).toFixed(1) + ' ' + Y(v).toFixed(1) + ' '; });
  let area = '';
  let seg: [number, number][] = [];
  const flush = () => {
    if (seg.length > 1) area += `M${X(seg[0][0])} ${Y(0)} ` + seg.map(([i, v]) => `L${X(i).toFixed(1)} ${Y(v).toFixed(1)}`).join(' ') + ` L${X(seg[seg.length - 1][0])} ${Y(0)}Z `;
    seg = [];
  };
  ser.forEach((v, i) => { if (refills.includes(i)) { seg.push([i - 0.001, ser[i - 1]]); flush(); seg.push([i, 0]); } seg.push([i, v]); });
  flush();
  const c = level < 20 ? 'var(--red)' : 'var(--blue)';
  const lx = X(n - 1), ly = Y(level), ex = X(n - 1 + fd);
  const emptyDay = new Date(Date.now() + fd * 86_400_000);

  const onMove = (e: React.MouseEvent<SVGRectElement>) => {
    const r = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
    const px = ((e.clientX - r.left) * W) / r.width;
    const i = Math.max(0, Math.min(n - 1, Math.round((px - pl) / sx)));
    setHi(i);
    tip.show(
      <><b>{dayLabel(days[i].day, { weekday: 'long', day: 'numeric', month: 'long' })}</b>
        <div className="tr"><span>{S.hh.level}</span>{days[i].level == null ? S.hh.noReading : `${Math.round(ser[i])} %`}</div>
        {refills.includes(i) && <div className="tr"><span>{S.hh.event}</span>{S.hh.refill}</div>}</>,
      e.clientX, e.clientY,
    );
  };

  return (
    <div className="chart" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={S.hh.chart}>
        <defs>
          <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" style={{ stopColor: 'var(--blue)', stopOpacity: 0.16 }} /><stop offset="1" style={{ stopColor: 'var(--blue)', stopOpacity: 0 }} />
          </linearGradient>
        </defs>
        {[0, 25, 50, 75, 100].map((t) => (
          <g key={t}>
            <line className="gridl" x1={pl} x2={W - pr + 40} y1={Y(t)} y2={Y(t)} strokeDasharray={t ? '2 4' : undefined} />
            <text className="ax" x={pl - 8} y={Y(t) + 4} textAnchor="end">{t} %</text>
          </g>
        ))}
        <line x1={pl} x2={W - pr + 40} y1={Y(20)} y2={Y(20)} style={{ stroke: 'var(--red)' }} strokeWidth="1.2" strokeDasharray="5 4" opacity=".7" />
        <text className="ax" x={W - pr + 44} y={Y(20) + 4} style={{ fill: 'var(--red-ink)' }}>{S.hh.threshold}</text>
        <path d={area} fill={`url(#${id})`} />
        <path d={d} fill="none" style={{ stroke: 'var(--blue)' }} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {refills.map((i) => (
          <g key={i}>
            <line x1={X(i)} x2={X(i)} y1={Y(100)} y2={Y(0)} style={{ stroke: 'var(--green)' }} strokeWidth="1" strokeDasharray="2 3" opacity=".6" />
            <circle cx={X(i)} cy={Y(ser[i])} r="4.5" style={{ fill: 'var(--green)', stroke: 'var(--surface)' }} strokeWidth="2" />
          </g>
        ))}
        {daysLeft != null && <path d={`M${lx} ${ly}L${ex} ${Y(0)}`} fill="none" style={{ stroke: c }} strokeWidth="2" strokeDasharray="1 6" strokeLinecap="round" />}
        <circle cx={lx} cy={ly} r="9" style={{ fill: c }} opacity=".18" />
        <circle cx={lx} cy={ly} r="5" style={{ fill: c, stroke: 'var(--surface)' }} strokeWidth="2" />
        {daysLeft != null && <>
          <circle cx={ex} cy={Y(0)} r="4" style={{ fill: 'var(--surface)', stroke: c }} strokeWidth="2" />
          <text className="ax" x={ex + 9} y={Y(0) + 4} style={{ fill: 'var(--ink-2)', fontWeight: 600 }}>{S.hh.emptyAbout(dayLabel(emptyDay, { weekday: 'short', day: 'numeric' }))}</text>
        </>}
        {[0, 15, 30, 45].map((i) => <text key={i} className="ax" x={X(i)} y={H - 8} textAnchor="middle">{dayLabel(days[i].day)}</text>)}
        <text className="ax" x={lx} y={H - 8} textAnchor="middle" style={{ fill: 'var(--ink)', fontWeight: 600 }}>{S.todayShort}</text>
        {hi != null && <>
          <line x1={X(hi)} x2={X(hi)} y1={pt} y2={pt + ih} style={{ stroke: 'var(--ink-4)' }} strokeWidth="1" opacity=".5" />
          <circle cx={X(hi)} cy={Y(ser[hi])} r="4" style={{ fill: 'var(--blue)', stroke: 'var(--surface)' }} strokeWidth="2" />
        </>}
        <rect x={pl} y={pt} width={Math.max(0, lx - pl)} height={ih} fill="transparent" onMouseMove={onMove} onMouseLeave={() => { setHi(null); tip.hide(); }} />
      </svg>
    </div>
  );
}
